<script setup>
    import { ref, useEnvMapLoader } from '#imports';
    import {
        ACESFilmicToneMapping,
        AgXToneMapping,
        CineonToneMapping,
        ColorManagement,
        CustomToneMapping,
        LinearToneMapping,
        NeutralToneMapping,
        NoToneMapping,
        ReinhardToneMapping,
    } from 'three';
    import { watch } from 'vue';

    import { GLTFLoader, KTXLoader, TextureLoader } from '@resn/gozer-loading';
    import { Renderer, useResize } from '@resn/gozer-vue';

    import GlobalWrapper from '~/components/GlobalWrapper.vue';
    import PageLoader from '~/components/PageLoader.vue';
    import { runGpuTierProbe } from '~/composables/useGpuTier';
    import { NoiseTextures } from '~/providers/NoiseTexturesProvider';
    import { PostProcessing } from '~/providers/PostProcessingComposer';
    import { ThreePrerender } from '~/providers/ThreePrerender';

    const refRenderer = ref(null);

    const { loadEnvMap } = useEnvMapLoader();

    const MAX_DPR = 2;

    const onRenderSetup = ({ renderer }) => {
        // Apply DPR before child onMounted runs composer.setup(). With autoResize=false
        // the only other DPR write is refRenderer watch onResize, which fires too late.
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_DPR));

        KTXLoader.initGlobalKTX2Loader('/three/basis/', renderer);
        KTXLoader.setGlobals({ renderer });
        TextureLoader.setGlobals({ renderer });
        GLTFLoader.setGlobals({ renderer, ktxLoader: KTXLoader.globalKTX2Loader });

        renderer.setClearColor(0x000000, 1);
        ColorManagement.enabled = true;

        window.renderer = renderer;
        window.toneMappingOptions = {
            NoToneMapping: NoToneMapping,
            LinearToneMapping: LinearToneMapping,
            ReinhardToneMapping: ReinhardToneMapping,
            CineonToneMapping: CineonToneMapping,
            ACESFilmicToneMapping: ACESFilmicToneMapping,
            AgXToneMapping: AgXToneMapping,
            NeutralToneMapping: NeutralToneMapping,
            CustomToneMapping: CustomToneMapping,
        };

        renderer.toneMapping = NeutralToneMapping;

        loadEnvMap(renderer, '/textures/env-map/env-map-global.exr');

        void runGpuTierProbe({ glContext: renderer.getContext(), force: true });
    };

    // Renderer using viewport for resize which is incorrect
    // Need to override the resize and camera aspect with correct values
    const onResize = () => {
        if (!refRenderer.value) return;

        const { width, height } = viewport;
        const { camera, orthoCamera } = refRenderer.value.provider;

        camera.aspect = width / height;
        camera.updateProjectionMatrix();

        orthoCamera.left = -width / 2;
        orthoCamera.right = width / 2;
        orthoCamera.top = height / 2;
        orthoCamera.bottom = -height / 2;
        orthoCamera.updateProjectionMatrix();

        refRenderer.value.resize({ width, height });
    };

    const viewport = useResize(onResize);

    watch(refRenderer, onResize);
</script>

<template>
    <GlobalWrapper>
        <Renderer
            ref="refRenderer"
            @setupComplete="onRenderSetup"
            :stencil="true"
            :maxDpr="MAX_DPR"
            :autoResize="false"
        >
            <ThreePrerender>
                <PostProcessing>
                    <NoiseTextures>
                        <main id="main">
                            <slot />
                        </main>
                    </NoiseTextures>
                </PostProcessing>
            </ThreePrerender>
        </Renderer>
        <PageLoader />
    </GlobalWrapper>
</template>

<style lang="scss" scoped>
    #main {
        min-height: 100vh;
    }
</style>
